import { IBillGroupResp, IDunningStepCreateSchema } from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { formatCurrency, toDateShort } from '@monetize/utils/core';
import { useGetBillGroupStatsById } from '../../../../../api/accountsService';
import { MDivider, MGrid } from '../../../../../components/Monetize';
import { useShowPastDueFields } from '../../../../../hooks';
import { getDaysOutstanding } from '../../../../../utils/invoices';
import { BillGroupDetailsCard } from '../../BillGroupDetailsCard';

interface BillGroupDetailHeaderProps {
  billGroup: IBillGroupResp;
  dunningStep?: Maybe<IDunningStepCreateSchema>;
}

export const BillGroupDetailHeader = ({
  billGroup,
  dunningStep,
}: BillGroupDetailHeaderProps) => {
  const { currency } = billGroup;
  const showPastDue = useShowPastDueFields();

  const { data: stats } = useGetBillGroupStatsById(billGroup.id);

  const pastDueAmount = billGroup.pastDueAmountOutstanding ?? 0;
  const daysOutstanding = getDaysOutstanding(billGroup.minimumInvoiceDueDate);

  return (
    <>
      <MGrid
        justifyItems="start"
        gridTemplateColumns="repeat(5, 1fr 0.1fr) 1fr"
        columnGap={2}
        mb={10}
      >
        {billGroup.nextInvoiceDate && (
          <>
            <BillGroupDetailsCard
              label="Next Invoice Date"
              value={toDateShort(billGroup.nextInvoiceDate)}
            />
            <MDivider
              orientation="vertical"
              variant="thick"
              justifySelf="center"
            />
          </>
        )}
        {billGroup.dunningStepId && (
          <>
            <BillGroupDetailsCard
              label="Dunning Step"
              value={dunningStep?.stepName || ''}
            />
            <MDivider
              orientation="vertical"
              variant="thick"
              justifySelf="center"
            />
          </>
        )}
        {stats && (
          <>
            <BillGroupDetailsCard
              label="Open Quotes"
              value={stats.openQuotes.toString()}
            />
            <MDivider
              orientation="vertical"
              variant="thick"
              justifySelf="center"
            />
            <BillGroupDetailsCard label="Currency" value={currency} />
            <MDivider
              orientation="vertical"
              variant="thick"
              justifySelf="center"
            />
            <BillGroupDetailsCard
              label="Invoiced"
              value={formatCurrency(stats.invoicedAmount, { currency })}
            />
            <MDivider
              orientation="vertical"
              variant="thick"
              justifySelf="center"
            />
            <BillGroupDetailsCard
              label="Amount Outstanding"
              value={formatCurrency(
                billGroup.amountOutstanding ?? stats.outstandingAmount,
                { currency },
              )}
            />
          </>
        )}
      </MGrid>
      {showPastDue && (
        <MGrid
          justifyItems="start"
          gridTemplateColumns="repeat(5, 1fr 0.1fr) 1fr"
          columnGap={2}
          mb={10}
        >
          <BillGroupDetailsCard
            label="Past Due Amount"
            value={formatCurrency(pastDueAmount, { currency })}
            valueColor={pastDueAmount > 0 ? 'tRed.base' : undefined}
          />
          <MDivider
            orientation="vertical"
            variant="thick"
            justifySelf="center"
          />
          <BillGroupDetailsCard
            label="Past Due Invoices"
            value={(billGroup.pastDueInvoiceCount ?? 0).toString()}
            valueColor={
              (billGroup.pastDueInvoiceCount ?? 0) > 0 ? 'tRed.base' : undefined
            }
          />
          <MDivider
            orientation="vertical"
            variant="thick"
            justifySelf="center"
          />
          <BillGroupDetailsCard
            label="Days Outstanding"
            value={daysOutstanding.toString()}
            valueColor={daysOutstanding > 0 ? 'tRed.base' : undefined}
          />
        </MGrid>
      )}
    </>
  );
};
